import React, { useContext, useState, type FC, type PropsWithChildren } from 'react';

import { getI18nData } from '@lucid/utils';

import PauseOverlay from '../buttons/pause-overlay';
import Box from '../box/box';
import Text from '../text/text';
import { type Lucid } from '../../types/types';
import LayoutContext from '../context/layout-context';

interface GifWrapperProps {
  /** The i18n translation data. */
  /** Should include in query:
   ** mktg_ct_add_on_pause_animation_label
   ** mktg_ct_add_on_play_animation_label
   */
  i18nData?: Lucid.I18n.DataEdges;
  /** Allows the component to get additional styles. */
  className?: string;
}

const GifWrapper: FC<PropsWithChildren<GifWrapperProps>> = ({ children, className, i18nData, ...rest }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const { shortCode } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, shortCode);

  return (
    <PauseOverlay
      aria-label={
        isPlaying
          ? i18n('mktg-ct-add-on-pause-animation-label') || 'Click or press Enter to pause animation'
          : i18n('mktg-ct-add-on-play-animation-label') || 'Click or press Enter to play animation'
      }
      css={{
        '& img:nth-child(2)': {
          opacity: isPlaying ? 1 : 0,
        },
      }}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      isVideoPlaying={isPlaying}
      onClick={() => setIsPlaying(!isPlaying)}
    >
      {!isPlaying && (
        <Box
          className="gif-wrapper"
          css={theme => ({
            position: 'absolute',
            height: '100%',
            width: '100%',
            background: theme.colors.coolGrey[5],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 'inherit',
            border: `2px solid ${theme.colors.coolGrey[100]}`,
            borderRadius: theme.radii.r16,
          })}
        >
          <img
            {...rest}
            alt="paused gif overlay"
            css={{ height: '80px', width: '80px', margin: '0 -43px 0 0 !important' }}
            src="https://cdn-cashy-static-assets.lucidchart.com/icons/action-icons/pageoutline24.svg"
          />
          <Text
            css={theme => ({ background: theme.colors.coolGrey[5], margin: '12px 0 0 0 !important' })}
            variant="labelS400"
          >
            GIF is paused
          </Text>
        </Box>
      )}
      {children}
    </PauseOverlay>
  );
};

export { GifWrapper };
export default GifWrapper;
